import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { Button } from "../../Components/Button";
import StarRating from "../../Components/StarRating";
import { VIDEO_CALL_ADDON_ID, AUDIO_CALL_ADDON_ID } from "../../Constants";
import {
  CheckIcon,
  ClockIcon,
  ChatIcon,
  MicrophoneIcon,
  DesktopComputerIcon,
} from "@heroicons/react/solid";
import {
  requestGetConsultantByID,
  requestGetConsultantWebinars,
  requestPaymentMethods,
  useCheckoutDispatch,
  useCheckoutState,
  useConsultantDispatch,
  useConsultantState,
  useWebinarDispatch,
  useWebinarState,
} from "../../Context";
import { ConsultantDetailSkeleton } from "../../Components/ConsultantDetailSkeleton";
import { Alert } from "../../Components/Alert";
import { calculateExperience, isConsultantOutOfHours } from "../../Utils";
import NewsCardSkeleton from "../../Components/NewsCardSkeleton";
import WebinarCard from "../../Components/WebinarCard";

function getInitials(fullName = "User") {
  var allNames = fullName.trim().split(" ");
  var initials = allNames.reduce((acc, curr, index) => {
    if (index === 0 || index === allNames.length - 1) {
      acc = `${acc}${curr.charAt(0).toUpperCase()}`;
    }
    return acc;
  }, "");
  initials = initials.toUpperCase();
  return initials;
}

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const ConsultantDetail = () => {
  const { t } = useTranslation();

  const { id } = useParams();

  const dispatch = useConsultantDispatch();
  const checkoutDispatch = useCheckoutDispatch();
  const webinarDispatch = useWebinarDispatch();
  const { consultant, error, loading } = useConsultantState();
  const {
    paymentStatus,
    error: paymentStatusError,
    loading: paymentStatusLoading,
  } = useCheckoutState();
  const { consultantWebinars, consultantWebinarLoading } = useWebinarState();

  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);

  const experienceYear = calculateExperience(consultant);

  useEffect(() => {
    // Fetch consultant data by ID
    if (id) {
      requestGetConsultantByID(dispatch, { id });
      requestPaymentMethods(checkoutDispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (consultant?.id) {
      requestGetConsultantWebinars(webinarDispatch, { id: consultant.id });
    }
  }, [webinarDispatch, consultant?.id]);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  const handleConsultationClick = () => {
    navigate(`/consultation/form/${consultant.id}`);
  };

  const getAvailabilityText = () => {
    if (
      !consultant?.activeDays ||
      !consultant?.startTimes ||
      !consultant?.endTimes
    ) {
      return t("consultant.status.scheduleNotAvailable");
    }

    const activeDaysText = DAYS.filter(
      (_, index) => consultant.activeDays[index],
    )
      .map((day) => t(day))
      .join(", ");

    // Format consultant times (simple hour formatting)
    const formatHour = (hour) => `${hour.toString().padStart(2, "0")}:00`;
    const startTime = formatHour(consultant.startTimes[0] || 0);
    const endTime = formatHour(consultant.endTimes[0] || 0);

    return `${activeDaysText} (${startTime} - ${endTime})`;
  };

  const getServicePrice = (serviceType) => {
    const priceMap = {
      onlineChat: {
        consultantPrice: consultant?.consultationPrice,
        defaultKey: "online-consultation",
      },
      audioVideo: {
        consultantPrice: consultant?.callPrice,
        defaultKey: VIDEO_CALL_ADDON_ID,
      },
      audioCall: {
        consultantPrice: consultant?.audioPrice,
        defaultKey: AUDIO_CALL_ADDON_ID,
      },
      videoConference: {
        consultantPrice: consultant?.zoomPrice,
        defaultKey: "zoom-addon",
      },
    };

    const { consultantPrice, defaultKey } = priceMap[serviceType];

    // Return consultant price if set, otherwise return default from payment status
    return consultantPrice || paymentStatus?.prices?.[defaultKey]?.price || 0;
  };

  // Strike-through anchor: the product's `original` from prices — only shown
  // when no consultant override is set and it beats the effective price.
  const getServiceOriginal = (serviceType) => {
    const map = {
      audioVideo: {
        override: consultant?.callPrice,
        defaultKey: VIDEO_CALL_ADDON_ID,
      },
      audioCall: {
        override: consultant?.audioPrice,
        defaultKey: AUDIO_CALL_ADDON_ID,
      },
      videoConference: {
        override: consultant?.zoomPrice,
        defaultKey: "zoom-addon",
      },
    };
    const entry = map[serviceType];
    if (!entry || entry.override) return null;
    const original = paymentStatus?.prices?.[entry.defaultKey]?.original;
    return original > getServicePrice(serviceType) ? original : null;
  };

  if (loading || paymentStatusLoading) {
    return <ConsultantDetailSkeleton />;
  }

  if (error || paymentStatusError) {
    return (
      <div className="flex flex-col gap-6">
        <div className="bg-white min-h-full rounded-lg p-4">
          <Alert type="error" title="Error" message={error?.message} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow-sm max-w-7xl mx-auto overflow-hidden px-4 py-6 sm:px-6 rounded-lg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Consultant Info */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex flex-col sm:flex-row gap-6">
                {/* Profile Image */}
                <div className="flex-shrink-0">
                  {consultant.user?.imageUrl && !imageError ? (
                    <img
                      src={consultant.user.imageUrl}
                      alt="Profile"
                      className="rounded-lg object-cover w-32 h-32"
                      onError={handleImageError}
                      onLoad={handleImageLoad}
                    />
                  ) : (
                    <div className="rounded-lg w-32 h-32 flex items-center justify-center overflow-hidden bg-gray-200 text-4xl text-gray-600">
                      {getInitials(consultant.user?.name)}
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {consultant.user?.name}
                  </h2>

                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center">
                      <StarRating rating={consultant.rating} />
                      <span className="ml-2 text-sm text-gray-600">
                        {consultant.rating} ({consultant.totalConsultation})
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      <span>
                        {experienceYear} {t("Years of Experience")}
                      </span>
                    </div>

                    {consultant.city && (
                      <div className="flex items-center">
                        <span className="h-4 w-4 mr-2">📍</span>
                        <span>{consultant.city}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Services */}
            {consultant.services?.items.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("Services")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {consultant.services?.items.map((item) => (
                    <span
                      key={item.service.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700"
                    >
                      {t(item.service.name)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Availability */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t("Availability")}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  <span>{getAvailabilityText()}</span>
                </div>

                <div className="flex flex-wrap gap-3">
                  {consultant.acceptOnline && (
                    <div className="flex items-center text-sm text-green-600">
                      <ChatIcon className="h-4 w-4 mr-1" />
                      <span>{t("consultant.services.onlineChat")}</span>
                    </div>
                  )}
                  {consultant.acceptCall &&
                    paymentStatus?.prices?.[VIDEO_CALL_ADDON_ID]?.available && (
                      <div className="flex items-center text-sm text-green-600">
                        <MicrophoneIcon className="h-4 w-4 mr-1" />
                        <span>{t("consultant.services.audioVideo")}</span>
                      </div>
                    )}
                  {consultant.acceptZoom &&
                    paymentStatus?.prices?.["zoom-addon"]?.available && (
                      <div className="flex items-center text-sm text-green-600">
                        <DesktopComputerIcon className="h-4 w-4 mr-1" />
                        <span>{t("consultant.services.videoConference")}</span>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Pricing & Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t("consultant.pricing.title")}
              </h3>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <div className="flex items-center">
                    <ChatIcon className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {t("consultant.services.onlineChat")}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    <NumericFormat
                      displayType={"text"}
                      value={getServicePrice("onlineChat")}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp "}
                    />
                  </div>
                </div>

                {Boolean(
                  consultant.acceptCall &&
                  paymentStatus?.prices?.[VIDEO_CALL_ADDON_ID]?.available,
                ) && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <div className="flex items-center">
                      <MicrophoneIcon className="h-4 w-4 mr-2 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {t("consultant.services.audioVideo")}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {getServiceOriginal("audioVideo") && (
                        <span className="line-through text-gray-400 mr-1">
                          <NumericFormat
                            displayType={"text"}
                            value={getServiceOriginal("audioVideo")}
                            thousandSeparator={"."}
                            decimalSeparator={","}
                            prefix={"Rp "}
                          />
                        </span>
                      )}
                      <NumericFormat
                        displayType={"text"}
                        value={getServicePrice("audioVideo")}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp "}
                      />
                    </div>
                  </div>
                )}

                {Boolean(
                  consultant.acceptCall &&
                  paymentStatus?.prices?.[AUDIO_CALL_ADDON_ID]?.available,
                ) && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <div className="flex items-center">
                      <MicrophoneIcon className="h-4 w-4 mr-2 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {t("consultant.services.audioCall")}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {getServiceOriginal("audioCall") && (
                        <span className="line-through text-gray-400 mr-1">
                          <NumericFormat
                            displayType={"text"}
                            value={getServiceOriginal("audioCall")}
                            thousandSeparator={"."}
                            decimalSeparator={","}
                            prefix={"Rp "}
                          />
                        </span>
                      )}
                      <NumericFormat
                        displayType={"text"}
                        value={getServicePrice("audioCall")}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp "}
                      />
                    </div>
                  </div>
                )}

                {Boolean(
                  consultant.acceptZoom &&
                  paymentStatus?.prices?.["zoom-addon"]?.available,
                ) && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <div className="flex items-center">
                      <DesktopComputerIcon className="h-4 w-4 mr-2 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {t("consultant.services.videoConference")}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {getServiceOriginal("videoConference") && (
                        <span className="line-through text-gray-400 mr-1">
                          <NumericFormat
                            displayType={"text"}
                            value={getServiceOriginal("videoConference")}
                            thousandSeparator={"."}
                            decimalSeparator={","}
                            prefix={"Rp "}
                          />
                        </span>
                      )}
                      <NumericFormat
                        displayType={"text"}
                        value={getServicePrice("videoConference")}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp "}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <Button
                  onClick={handleConsultationClick}
                  title="consultant.action.startConsultation"
                  disabled={
                    !consultant.active || isConsultantOutOfHours(consultant)
                  }
                  className="w-full"
                />
              </div>

              {consultant.active && !isConsultantOutOfHours(consultant) ? (
                <div className="mt-4 flex items-center justify-center text-sm text-green-600">
                  <CheckIcon className="h-4 w-4 mr-1" />
                  <span>{t("consultant.status.available")}</span>
                </div>
              ) : (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-center text-sm text-yellow-800">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    <span>{t("consultant.status.offline")}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {(consultantWebinarLoading || consultantWebinars?.length > 0) && (
        <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm mt-4">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-slate-900 tracking-tight">
                {t("webinarExperience")}
              </h3>
              <a
                href="/webinar"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors duration-200"
              >
                {t("View All")} →
              </a>
            </div>
            <div className="block md:hidden">
              <div className="grid grid-cols-1 gap-4">
                {consultantWebinarLoading
                  ? Array.from({ length: 4 }, (_, index) => (
                      <NewsCardSkeleton key={index} />
                    ))
                  : consultantWebinars.map((webinar) => (
                      <a key={webinar.id} href={`/webinar/${webinar.id}`}>
                        <WebinarCard webinar={webinar} />
                      </a>
                    ))}
              </div>
            </div>

            <div className="hidden md:block overflow-x-auto">
              <div className="flex gap-4 pb-2" style={{ width: "max-content" }}>
                {consultantWebinarLoading
                  ? Array.from({ length: 4 }, (_, index) => (
                      <div key={index} className="flex-shrink-0 w-80">
                        <NewsCardSkeleton />
                      </div>
                    ))
                  : consultantWebinars.map((webinar) => (
                      <a
                        key={webinar.id}
                        href={`/webinar/${webinar.id}`}
                        className="flex flex-shrink-0 border border-gray-200 rounded-lg p-4 w-80"
                      >
                        <WebinarCard webinar={webinar} />
                      </a>
                    ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
